import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// PlannerNavShortcuts — global "go to planner view" chord, active anywhere in
// the Notes & Tasks suite:
//     p then d  → Planner · Daily
//     p then w  → Planner · Weekly
//     p then m  → Planner · Monthly
//
// Press "p", then one of d/w/m within ~1.5s. Renders nothing; drop one into
// each suite shell (Notes / Tasks / Planner).
//
// Implementation notes:
//  * Listens in the CAPTURE phase so it runs *before* each shell's own
//    single-key shortcuts (e.g. Tasks maps bare "d" → Today). When the chord's
//    second key resolves we stopImmediatePropagation() so the shell handler
//    never also fires.
//  * Navigates to /notes/planner?view=… (handles cross-page jumps via a fresh
//    mount) AND dispatches a `planner:navigate` CustomEvent so an already-
//    mounted PlannerAppShell switches its view in place (it strips the query
//    param after first mount, so URL alone can't re-drive it).
//  * Ignores the chord while typing in inputs / textareas / contenteditable,
//    and when any modifier key is held.
// ---------------------------------------------------------------------------
const VIEW_FOR_KEY = { d: 'daily', w: 'weekly', m: 'monthly' };
const CHORD_TIMEOUT_MS = 1500;

export function PlannerNavShortcuts() {
  const navigate = useNavigate();

  useEffect(() => {
    let pending = false;
    let timer = null;
    const reset = () => { pending = false; if (timer) { clearTimeout(timer); timer = null; } };

    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) { reset(); return; }
      const el = e.target;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (typing) { reset(); return; }
      const k = (e.key || '').toLowerCase();

      if (pending) {
        const view = VIEW_FOR_KEY[k];
        if (view) {
          // Resolve the chord — consume the key so shell handlers don't act on it.
          e.preventDefault();
          e.stopImmediatePropagation();
          reset();
          try { window.dispatchEvent(new CustomEvent('planner:navigate', { detail: { view } })); } catch { /* noop */ }
          navigate(`/notes/planner?view=${view}`);
          return;
        }
        // Any other key cancels the chord (and is otherwise left untouched).
        reset();
        return;
      }

      if (k === 'p') {
        // Start the chord. Don't consume "p" (nothing else uses it) so this
        // stays maximally non-intrusive.
        pending = true;
        timer = setTimeout(() => { pending = false; timer = null; }, CHORD_TIMEOUT_MS);
      }
    };

    window.addEventListener('keydown', onKey, true); // capture phase
    return () => { window.removeEventListener('keydown', onKey, true); reset(); };
  }, [navigate]);

  return null;
}

export default PlannerNavShortcuts;
